/**
 * Midjourney model-repository definition (via APIFrame).
 *
 * v1 scope: text-to-image only (one imagine job -> 4 split images). Prompt
 * images are additive Midjourney references, not an edit topology. No
 * variations/rerolls.
 */

import {
  ModelFamily,
  ModelSurface,
  ModelTopology,
  ReferenceTransportStrategy,
} from '../../config/models/schema.js';
import {
  DEFAULT_MIDJOURNEY_VERSION,
  MIDJOURNEY_DIALS,
  MIDJOURNEY_STYLE_WEIGHT,
} from './parameters.js';

const MIDJOURNEY_ENDPOINT = 'apiframe:midjourney/imagine';
const MIDJOURNEY_TIMING = {
  source: 'curated-existing-config',
  base: 60,
  baseSeconds: 60,
  batchExtraMult: 0.1,
};
// Midjourney style reference by numeric code (--sref <code> / "random") plus
// its style weight (--sw). Distinct from an image style reference (a
// 'style'-role image on the reference-chip system → --sref <url>); codes and
// image URLs compose into one --sref clause. --sw has the most impact with
// codes (Midjourney docs).
const MIDJOURNEY_STYLE_REF = {
  codes: { switch: '--sref', placeholder: 'sref code(s) or "random"' },
  weight: MIDJOURNEY_STYLE_WEIGHT,
};

// Midjourney model version (--v), plus the Niji anime line (--niji). We send
// this explicitly on every job so output is pinned to a known version rather
// than whatever APIFrame happens to default to. V8.1 is Midjourney's current
// default (since 2026-06). Niji uses --niji <n>, everything else uses --v <n>.
const MIDJOURNEY_VERSION = {
  default: DEFAULT_MIDJOURNEY_VERSION,
  options: [
    { value: '8.1', label: 'V8.1' },
    { value: '8', label: 'V8' },
    { value: '7', label: 'V7' },
    { value: '6.1', label: 'V6.1' },
    { value: 'niji 6', label: 'Niji 6' },
  ],
};

// Only subject and style are exposed as intentional Midjourney reference roles.
// The service compiles subject by version: image prompt for V8/V8.1, --oref for
// V7, and --cref for V6.1/Niji 6. Style remains --sref. Scene/structure would
// collapse to plain image prompts, so we do not offer them as separate roles.
const IMAGE_REF_ROLES = ['subject', 'style'];

const mjReferences = {
  acceptedKinds: ['character', 'style'],
  acceptedMediaTypes: ['image'],
  roles: IMAGE_REF_ROLES,
  chips: IMAGE_REF_ROLES,
  limits: { total: 4, character: 3, style: 1 },
  transport: {
    strategy: ReferenceTransportStrategy.PROMPT_TAGS,
    fields: { images: 'imageUrls' },
    // Reference budgets are read from transport.buckets, not limits. Without
    // this the style slot declared above resolves to 0 and a 'style'-role chip
    // is dropped before it can reach the --sref clause the service builds for
    // it (midjourneyImageService normalizePromptImageRoles).
    buckets: {
      character: { max: 3, order: 0 },
      style: { max: 1, order: 1 },
    },
  },
  unsupportedBehavior: 'warn',
};

export const MIDJOURNEY_MODEL_DEFINITION = {
  // Version-agnostic registry id. The actual MJ model version is chosen at
  // request time via the version picker (--v, default 8.1) — NOT baked into
  // this id.
  id: 'midjourney',
  label: 'Midjourney',
  family: ModelFamily.IMAGE,
  provider: 'midjourney',
  iconKey: 'midjourney',
  // No list price in USD: APIFrame bills in its own credits. The note carries that to the
  // Settings card and the cost estimate instead of a bare "no price".
  pricing: { note: 'Billed in APIFrame credits' },
  qualityTier: 'midjourney',
  timing: MIDJOURNEY_TIMING,
  surfaces: [ModelSurface.IMAGE_GENERATE],
  topologies: [ModelTopology.TEXT_TO_IMAGE],
  features: {
    aspectRatio: { options: ['1:1', '3:4', '4:3', '2:3', '3:2', '9:16', '16:9'], default: '1:1' },
    // Midjourney always renders a 4-image grid per (billed) job — the count is
    // fixed at 4, so there is no batch-size control. min === max drives
    // supportsBatchSelection=false in imageModels.js.
    batch: { min: 4, max: 4 },
    promptImages: { generateMax: 4, editMax: 0 },
    negativePrompt: { enabled: true },
    midjourneyDials: MIDJOURNEY_DIALS,
    midjourneyStyleRef: MIDJOURNEY_STYLE_REF,
    midjourneyVersion: MIDJOURNEY_VERSION,
    guidanceScale: { enabled: false },
    inferenceSteps: { enabled: false },
    resolution: null,
    boardPresets: { enabled: false },
  },
  references: mjReferences,
  routes: {
    [ModelTopology.TEXT_TO_IMAGE]: {
      endpoint: MIDJOURNEY_ENDPOINT,
      features: {
        promptImages: { generateMax: 4, editMax: 0 },
        negativePrompt: { enabled: true },
        midjourneyDials: MIDJOURNEY_DIALS,
        midjourneyStyleRef: MIDJOURNEY_STYLE_REF,
        midjourneyVersion: MIDJOURNEY_VERSION,
      },
      references: mjReferences,
      timing: MIDJOURNEY_TIMING,
      execution: {
        type: 'provider-service-export',
        adapterId: 'image.reference.prompt-tags',
        providerKey: 'midjourney',
        exportName: 'generateMidjourneyImagine',
        endpoint: MIDJOURNEY_ENDPOINT,
      },
    },
  },
  prompt: { enhancerId: 'midjourney' },
  agent: { exposeAs: ['image-generator'] },
  presentation: {
    label: 'Midjourney',
    shortLabel: 'Midjourney',
    description: 'Midjourney access via APIFrame. One imagine job returns four images.',
    group: 'Image generation',
    tags: ['stylized'],
  },
  ui: {
    image: {
      attachedImageMode: 'reference',
      creativeAdapter: 'image-reference-positional',
      requiresPromptImages: false,
      supportsProps: true,
    },
  },
};
